import { Card, Tag, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import type { ReactNode } from 'react';
import type { TenantOrder } from '@/mocks/orders';
import { formatDateTime, formatVND } from '@/utils/format';
import { useOrderCreditLimitWarning } from './useOrderCreditLimitWarning';

interface OrderSummaryCustomerCardProps {
  order: TenantOrder;
  /** Nhãn + màu tag trạng thái đơn — do pop-up giải sẵn để CHUNG nguồn với tag ở "Tổng hợp tài chính". */
  statusLabel: string;
  statusColor: string;
}

interface InfoRowProps {
  label: string;
  children: ReactNode;
}

function InfoRow({ label, children }: InfoRowProps) {
  return (
    <tr>
      <td className="order-summary-customer-label">{label}:</td>
      <td className="order-summary-customer-value">{children}</td>
    </tr>
  );
}

/**
 * Khối "Thông tin khách hàng" của pop-up xem nhanh (TCR-1095, góp ý tester 13/08).
 *
 * Khác `CustomerCard` của trang chi tiết ở chỗ trình bày: mọi dòng là một cặp `Nhãn: giá trị`
 * căn thẳng mép trái như salework — kể cả tên khách hàng (trang chi tiết để tên đứng riêng, in
 * đậm, không nhãn). Ngày tạo và tag trạng thái nằm trong khối này chứ không nằm ở tiêu đề pop-up.
 *
 * Tag trạng thái nhận từ ngoài vào chứ không tự giải: pop-up đã giải một lần cho khối "Tổng hợp
 * tài chính", giải lần hai ở đây là mở lại đúng lỗi hai nguồn nhãn mà ticket này đi sửa.
 */
export function OrderSummaryCustomerCard({ order, statusLabel, statusColor }: OrderSummaryCustomerCardProps) {
  const { customer } = order;
  const creditWarning = useOrderCreditLimitWarning(order);

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    message.success(`Đã sao chép ${label}`);
  };

  const renderCopyable = (value: string | undefined, copyLabel: string) => {
    if (!value) return '---';
    return (
      <span className="order-summary-customer-copyable">
        <span>{value}</span>
        <CopyOutlined
          style={{ color: '#0958d9', cursor: 'pointer' }}
          onClick={() => handleCopy(value, copyLabel)}
        />
      </span>
    );
  };

  const amisId = customer?.amis_account_object_id;

  return (
    <Card className="order-detail-card" title="Thông tin khách hàng" size="small">
      <table className="order-summary-customer-table" data-testid="order-summary-customer">
        <tbody>
          <InfoRow label="Mã đơn hàng">
            <span className="order-summary-customer-copyable">
              <span>{order.order_number}</span>
              <CopyOutlined
                style={{ color: '#0958d9', cursor: 'pointer' }}
                onClick={() => handleCopy(order.order_number, 'mã đơn hàng')}
              />
              <Tag color={statusColor} data-testid="order-summary-status" style={{ marginInlineEnd: 0 }}>
                {statusLabel}
              </Tag>
            </span>
          </InfoRow>
          <InfoRow label="Ngày tạo">
            {order.created_at ? formatDateTime(order.created_at) : '---'}
          </InfoRow>
          <InfoRow label="Tên khách hàng">{customer?.name || '---'}</InfoRow>
          <InfoRow label="Số điện thoại">{renderCopyable(customer?.phone, 'số điện thoại')}</InfoRow>
          <InfoRow label="Mã KH">{renderCopyable(customer?.code, 'mã khách hàng')}</InfoRow>
          {/* Rút gọn nhãn "Mã đối tượng AMIS" theo góp ý tester: nhãn dài làm dòng bị gãy đôi
              trong cột hẹp của pop-up. Giá trị vẫn copy được ĐẦY ĐỦ, không phải bản rút gọn. */}
          <InfoRow label="Mã AMIS">
            {amisId ? (
              <span className="order-summary-customer-copyable">
                <span>{`${amisId.substring(0, 8)}...`}</span>
                <CopyOutlined
                  style={{ color: '#0958d9', cursor: 'pointer' }}
                  onClick={() => handleCopy(amisId, 'Mã AMIS')}
                />
              </span>
            ) : (
              '---'
            )}
          </InfoRow>
          {/* TCR-1825 — hiện cho khách B2B tổ chức (cùng nguồn `useB2BCreditLimit`
              với TCR-1862/TCR-1818). TCR-1892 — luôn hiện số công nợ/hạn mức, và
              cảnh báo theo công nợ DỰ KIẾN sau đơn này. Giữ khớp với `CustomerCard`
              để pop-up xem nhanh và trang chi tiết không nói hai điều khác nhau. */}
          {creditWarning && (
            <InfoRow label="Công nợ / Hạn mức">
              <span style={{ color: creditWarning.isOverLimit ? '#cf1322' : undefined, fontWeight: 600 }}>
                {formatVND(creditWarning.currentDebt)} / {formatVND(creditWarning.creditLimit)}
              </span>
            </InfoRow>
          )}
          {creditWarning?.isOverLimit && (
            <InfoRow label="Công nợ dự kiến">
              <span style={{ color: '#cf1322', fontWeight: 600 }}>
                {formatVND(creditWarning.debtAfter)}
                <Tag color="error" style={{ marginInlineStart: 6, marginInlineEnd: 0 }}>
                  Vượt {formatVND(creditWarning.excess)}
                </Tag>
              </span>
            </InfoRow>
          )}
        </tbody>
      </table>
    </Card>
  );
}

export default OrderSummaryCustomerCard;
